"use client";

/**
 * UnitPagination
 * - Simple prev/current/next navigation for content units
 * - Labels reflect physical page numbers in book_spreads mode
 */

import React from "react";
import { useTranslations } from "next-intl";
import { useEditorStore } from "./editorStore";

function labelForUnitIndex(units: any[], idx: number, layout: "book" | "spreads_only") {
  if (!units[idx]) return "";

  if (layout === "spreads_only") {
    const a = idx * 2 + 1;
    const b = idx * 2 + 2;
    return `${a}–${b}`;
  }

  if (idx === 0) return "1";
  if (idx === units.length - 1) {
    const spreadsCount = Math.max(0, units.length - 2);
    const last = 2 + 2 * spreadsCount;
    return String(last);
  }

  const a = 2 * idx;
  const b = 2 * idx + 1;
  return `${a}–${b}`;
}

export default function UnitPagination() {
  const store = useEditorStore();
  const t = useTranslations("editor.pagination");

  const doc = store.contentData;
  const units = Array.isArray(doc?.pages) ? doc.pages : [];
  const layoutMode = String(doc?.meta?.layout_mode || "pages");
  const layout = (String(doc?.meta?.content_layout || "book") === "spreads_only"
    ? "spreads_only"
    : "book") as "book" | "spreads_only";
  const idx = store.contentUnitIndex;
  const canPrev = idx > 0;
  const canNext = idx < units.length - 1;
  const label = labelForUnitIndex(units, idx, layout);

  if (store.activeDoc !== "content") return null;
  if (layoutMode !== "book_spreads") return null;

  return (
    <div className="flex items-center justify-center gap-5 pt-4">
      <button
        type="button"
        className="editor-pagination-button"
        disabled={!canPrev}
        onClick={() => store.setContentUnitIndex(idx - 1)}
      >
        <span aria-hidden="true">‹</span>
        {t("prev")}
      </button>

      <div className="editor-pagination-label">
        {layout === "book" ? t("pageLabel", { label }) : t("pagesLabel", { label })}
      </div>

      <button
        type="button"
        className="editor-pagination-button"
        disabled={!canNext}
        onClick={() => store.setContentUnitIndex(idx + 1)}
      >
        {t("next")}
        <span aria-hidden="true">›</span>
      </button>
    </div>
  );
}
